import * as React from "react";
import { Box, Typography, Paper } from "@mui/material";

interface FlipCardProps {
  image: string;
  title: string;
  description: {
    start: string;
    listItems: string[];
    end: string;
  };
  aspectRatio?: string;
}

export default function FlipCard({
  image,
  title,
  description,
  aspectRatio = "3 / 4",
}: FlipCardProps): React.JSX.Element {
  const [flipped, setFlipped] = React.useState(false);

  return (
    <Box
      sx={{
        perspective: 1200,
        width: "100%",
        mx: "auto",
      }}
      onClick={() => setFlipped((prev) => !prev)}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: aspectRatio,
          transformStyle: "preserve-3d",
          transition: "transform 0.6s",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front */}
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: 1,
              backgroundImage: `url(${image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="caption" fontWeight={600} fontSize="1.05rem">
              {title}
            </Typography>
          </Box>
        </Paper>

        {/* Back */}
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            borderRadius: 3,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            overflow: "auto",
          }}
        >
          <Typography variant="body1" color="text.primary" lineHeight={1.2}>
            {description.start}
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {description.listItems.map((item, idx) => (
              <Typography
                component="li"
                key={idx}
                variant="body1"
                color="text.primary"
                lineHeight={1.2}
              >
                {item}
              </Typography>
            ))}
          </Box>
          <Typography variant="body1" color="text.primary" lineHeight={1.2}>
            {description.end}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}
